import BigNumber from 'bignumber.js';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Address } from 'viem';

import { useGetSwapQuote, useRepay, useSwapTokensAndRepay } from 'clients/api';
import {
  LabeledInlineContent,
  NoticeWarning,
  QuaternaryButton,
  SelectTokenTextField,
  SpendingLimit,
  Toggle,
  TokenTextField,
  TransactionQueue,
} from 'components';
import { NULL_ADDRESS } from 'constants/address';
import { FULL_REPAYMENT_BUFFER_PERCENTAGE } from 'constants/fullRepaymentBuffer';
import useDebounceValue from 'hooks/useDebounceValue';
import { useGetContractAddress } from 'hooks/useGetContractAddress';
import { useGetUserSlippageTolerance } from 'hooks/useGetUserSlippageTolerance';
import { useIsFeatureEnabled } from 'hooks/useIsFeatureEnabled';
import { useSimulateBalanceMutations } from 'hooks/useSimulateBalanceMutations';
import useTokenApproval from 'hooks/useTokenApproval';
import { useAnalytics } from 'libs/analytics';
import { VError } from 'libs/errors';
import { useTranslation } from 'libs/translations';
import { useAccountAddress } from 'libs/wallet';
import type { Asset, AssetBalanceMutation, Pool } from 'types';
import {
  areTokensEqual,
  convertMantissaToTokens,
  convertTokensToMantissa,
  formatPercentageToReadableValue,
  formatTokensToReadableValue,
  getSwapToTokenAmount,
} from 'utilities';
import { ApyBreakdown } from '../../ApyBreakdown';
import { Footer } from '../../Footer';
import type { Approval } from '../../Footer/types';
import { calculateAmountDollars } from '../../calculateAmountDollars';
import { getMarketCapabilities } from '../../shared/getMarketCapabilities';
import { useGetVTokenWrapperKind } from '../../shared/useGetVTokenWrapperKind';
import { useGetWrapperExchangeRate } from '../../shared/useGetWrapperExchangeRate';
import { useGetOperationFormTokenBalances } from '../../useGetOperationFormTokenBalances';
import { Notice } from '../Notice';
import { calculatePercentageOfUserBorrowBalance } from './calculatePercentageOfUserBorrowBalance';
import { getFullRepayWalletRequirementTokens } from './getFullRepayWalletRequirement';
import TEST_IDS from './testIds';
import useForm, { type FormValues, type UseFormInput } from './useForm';
import {
  type ClosePositionRepaySource,
  useRouterClosePositionQueue,
} from './useRouterClosePositionQueue';
import { type RouterRepaySource, useRouterRepayQueue } from './useRouterRepayQueue';

export const PRESET_PERCENTAGES = [25, 50, 75, 100];

export interface RepayWithWalletBalanceFormProps {
  asset: Asset;
  pool: Pool;
  onSubmitSuccess?: () => void;
}

const RepayWithWalletBalanceForm: React.FC<RepayWithWalletBalanceFormProps> = ({
  asset,
  pool,
  onSubmitSuccess,
}) => {
  const isWrapUnwrapNativeTokenEnabled = useIsFeatureEnabled({
    name: 'wrapUnwrapNativeToken',
  });
  const isIntegratedSwapEnabled = useIsFeatureEnabled({
    name: 'integratedSwap',
  });
  const isPositionRouterEnabled = useIsFeatureEnabled({
    name: 'positionRouter',
  });

  const isIntegratedSwapFeatureEnabled =
    isIntegratedSwapEnabled &&
    // Check swap and supply action is enabled for underlying token
    !asset.disabledTokenActions.includes('swapAndRepay');

  const { accountAddress } = useAccountAddress();
  const { t } = useTranslation();
  const { captureAnalyticEvent } = useAnalytics();
  const isUserConnected = !!accountAddress;

  const { address: nativeTokenGatewayContractAddress } = useGetContractAddress({
    name: 'NativeTokenGateway',
    poolComptrollerContractAddress: pool.comptrollerAddress,
  });

  const canWrapNativeToken =
    isWrapUnwrapNativeTokenEnabled && !!asset.vToken.underlyingToken.tokenWrapped;

  // Probe the debt-leg underlying once, then centralize native capability
  // decisions in getMarketCapabilities instead of branching on wrapper tags in
  // the form.
  // Intentionally NOT gated on the positionRouter flag: useGetOperationFormTokenBalances needs wrapperKind for native TAO balance display and native alpha supply sources, which exist independently of the router flow.
  const { data: debtWrapperKind } = useGetVTokenWrapperKind(
    { underlyingAddress: asset.vToken.underlyingToken.address },
    { enabled: !!accountAddress },
  );
  const debtCapabilities = getMarketCapabilities({
    underlyingToken: asset.vToken.underlyingToken,
    wrapperKind: debtWrapperKind ?? 'unsupported',
  });

  const { tokenBalances, userWalletNativeTokenBalanceTokens, bittensorNativeSupplySources } =
    useGetOperationFormTokenBalances({
      poolComptrollerContractAddress: pool.comptrollerAddress,
      accountAddress,
      underlyingToken: asset.vToken.underlyingToken,
      wrapperKind: debtWrapperKind ?? 'unsupported',
      isIntegratedSwapFeatureEnabled,
      canWrapNativeToken,
      action: 'repay',
    });

  const shouldSelectNativeToken =
    canWrapNativeToken && userWalletNativeTokenBalanceTokens?.gt(asset.userWalletBalanceTokens);

  const initialFormValues: FormValues = useMemo(
    () => ({
      amountTokens: '',
      fromToken:
        shouldSelectNativeToken && asset.vToken.underlyingToken.tokenWrapped
          ? asset.vToken.underlyingToken.tokenWrapped
          : asset.vToken.underlyingToken,
      fixedRepayPercentage: undefined,
      acknowledgeHighPriceImpact: false,
    }),
    [shouldSelectNativeToken, asset.vToken.underlyingToken],
  );

  const [formValues, setFormValues] = useState<FormValues>(initialFormValues);

  // Reset form when user disconnects their wallet
  useEffect(() => {
    if (!accountAddress) {
      setFormValues(initialFormValues);
    }
  }, [accountAddress, initialFormValues]);

  // Reset form when initial values change, which indicates the base asset was changed
  useEffect(() => {
    setFormValues(initialFormValues);
  }, [initialFormValues]);

  const { address: swapRouterContractAddress } = useGetContractAddress({
    name: 'SwapRouterV2',
  });

  // a user is trying to wrap the chain's native token if
  // 1) the wrap/unwrap feature is enabled
  // 2) the selected fromToken is the native token
  // 3) the market's underlying token wraps the native token
  const isWrappingNativeToken = canWrapNativeToken && !!formValues.fromToken.isNative;

  const selectedBittensorNativeAlphaSource = bittensorNativeSupplySources.find((source) =>
    areTokensEqual(source.token, formValues.fromToken),
  );
  const isUsingBittensorNativeAlphaSource = !!selectedBittensorNativeAlphaSource;
  const nativeTaoToken = asset.vToken.underlyingToken.tokenWrapped;
  const isUsingBittensorNativeTaoSource =
    !!formValues.fromToken.isNative &&
    debtCapabilities.canUseNativeTao &&
    !!nativeTaoToken &&
    areTokensEqual(formValues.fromToken, nativeTaoToken);
  const useRouterFlow =
    isPositionRouterEnabled &&
    (isUsingBittensorNativeAlphaSource || isUsingBittensorNativeTaoSource);

  // Native TAO repays through the router as native wei but the debt is
  // denominated in wsTAO wrapper shares, so the form must convert with the live
  // wrapper rate (same "native base units per 1e18 shares" convention as
  // native alpha). Without it the 100% preset submits wsTAO shares mislabeled
  // as native TAO.
  const { data: nativeTaoExchangeRateBigInt } = useGetWrapperExchangeRate(
    { wrapperAddress: asset.vToken.underlyingToken.address },
    { enabled: isUsingBittensorNativeTaoSource },
  );

  // Debt is denominated in the wrapped (underlying) token; a selected native
  // source is denominated in its native unit. exchangeRate is "native base
  // units per 1e18 wrapper shares" (see routerSlippage.ts):
  // debtNativeTokens = debtWrapperTokens * exchangeRate / 10^nativeDecimals,
  // rounded up so a 100% repay never undershoots (the router refunds
  // over-payment). Memoized on primitive deps: the source objects are
  // recreated on every render, and an unstable BigNumber here would re-trigger
  // useForm's fixed-percentage effect on each render (setState loop).
  const nativeBorrowExchangeRate = isUsingBittensorNativeTaoSource
    ? nativeTaoExchangeRateBigInt
    : selectedBittensorNativeAlphaSource?.exchangeRate;
  const nativeBorrowDecimals = isUsingBittensorNativeTaoSource
    ? nativeTaoToken?.decimals
    : selectedBittensorNativeAlphaSource?.token.decimals;
  const fromTokenUserBorrowBalanceTokens = useMemo(() => {
    if (
      nativeBorrowExchangeRate === undefined ||
      nativeBorrowDecimals === undefined ||
      nativeBorrowExchangeRate <= 0n
    ) {
      return asset.userBorrowBalanceTokens;
    }

    return asset.userBorrowBalanceTokens
      .times(nativeBorrowExchangeRate.toString())
      .dividedBy(new BigNumber(10).pow(nativeBorrowDecimals))
      .dp(nativeBorrowDecimals, BigNumber.ROUND_CEIL);
  }, [nativeBorrowExchangeRate, nativeBorrowDecimals, asset.userBorrowBalanceTokens]);

  const isUsingSwap =
    isIntegratedSwapFeatureEnabled &&
    !isWrappingNativeToken &&
    !isUsingBittensorNativeAlphaSource &&
    !isUsingBittensorNativeTaoSource &&
    !areTokensEqual(asset.vToken.underlyingToken, formValues.fromToken);

  let spenderAddress: Address | undefined = asset.vToken.address;
  if (isWrappingNativeToken) {
    spenderAddress = nativeTokenGatewayContractAddress;
  } else if (isUsingSwap) {
    spenderAddress = swapRouterContractAddress;
  }

  const {
    isTokenApproved: isFromTokenApproved,
    walletSpendingLimitTokens: fromTokenWalletSpendingLimitTokens,
    revokeWalletSpendingLimit: revokeFromTokenWalletSpendingLimit,
    isRevokeWalletSpendingLimitLoading: isRevokeFromTokenWalletSpendingLimitLoading,
  } = useTokenApproval({
    token: formValues.fromToken,
    spenderAddress,
    accountAddress,
  });

  const { mutateAsync: onRepay, isPending: isRepayLoading } = useRepay();

  const { mutateAsync: onSwapAndRepay, isPending: isSwapAndRepayLoading } = useSwapTokensAndRepay();

  const { userSlippageTolerancePercentage } = useGetUserSlippageTolerance();

  const {
    execute: executeRouterRepayQueue,
    isRunning: isRouterRepayQueueRunning,
    items: routerRepayQueueItems,
  } = useRouterRepayQueue();
  const {
    execute: executeRouterClosePositionQueue,
    isRunning: isRouterClosePositionQueueRunning,
    items: routerClosePositionQueueItems,
  } = useRouterClosePositionQueue();

  const [bundleRedeemEnabled, setBundleRedeemEnabled] = useState(false);
  const [bundleRedeemAmountTokens, setBundleRedeemAmountTokens] = useState('');

  const collateralAssetsWithBalance = useMemo(
    () =>
      pool.assets.filter(
        (a) =>
          a.userSupplyBalanceTokens.isGreaterThan(0) &&
          a.userCollateralFactor > 0 &&
          !areTokensEqual(a.vToken.underlyingToken, asset.vToken.underlyingToken),
      ),
    [pool.assets, asset.vToken.underlyingToken],
  );
  const collateralAssetToClose =
    collateralAssetsWithBalance.length === 1 ? collateralAssetsWithBalance[0] : undefined;

  const { data: collateralWrapperKind } = useGetVTokenWrapperKind(
    {
      underlyingAddress: collateralAssetToClose?.vToken.underlyingToken.address ?? NULL_ADDRESS,
    },
    { enabled: !!collateralAssetToClose && isPositionRouterEnabled },
  );
  const collateralCapabilities = collateralAssetToClose
    ? getMarketCapabilities({
        underlyingToken: collateralAssetToClose.vToken.underlyingToken,
        wrapperKind: collateralWrapperKind ?? 'unsupported',
      })
    : undefined;

  // canRouteNativeRepay is wsTAO-only (see getMarketCapabilities): it gates
  // bundled close-position, the only flow that needs it. Plain native repay is
  // already covered by useRouterFlow above.
  const canBundleClosePosition =
    useRouterFlow &&
    debtCapabilities.canRouteNativeRepay &&
    !!collateralAssetToClose &&
    !!collateralCapabilities?.canUnwrapNativeCollateral;

  const bundleRedeemAmountMissing =
    canBundleClosePosition && bundleRedeemEnabled && !(Number(bundleRedeemAmountTokens) > 0);
  const bundleRedeemExceedsBalance =
    canBundleClosePosition &&
    bundleRedeemEnabled &&
    !!collateralAssetToClose &&
    Number(bundleRedeemAmountTokens) > 0 &&
    new BigNumber(bundleRedeemAmountTokens).isGreaterThan(
      collateralAssetToClose.userSupplyBalanceTokens,
    );

  const isSubmitting =
    isRepayLoading ||
    isSwapAndRepayLoading ||
    isRouterRepayQueueRunning ||
    isRouterClosePositionQueueRunning;

  const onSubmit: UseFormInput['onSubmit'] = useCallback(
    async ({ fromToken, fromTokenAmountTokens, swapQuote, fixedRepayPercentage }) => {
      const repayFullLoan = fixedRepayPercentage === 100;
      const amountMantissa = convertTokensToMantissa({
        value: new BigNumber(fromTokenAmountTokens.trim()),
        token: fromToken,
      });

      if (useRouterFlow) {
        let repaySource: ClosePositionRepaySource = { kind: 'native-tao' };
        if (isUsingBittensorNativeAlphaSource) {
          if (!selectedBittensorNativeAlphaSource) {
            throw new Error('Missing selected native alpha source');
          }
          repaySource = {
            kind: 'native-alpha',
            source: selectedBittensorNativeAlphaSource,
          };
        }

        if (
          canBundleClosePosition &&
          bundleRedeemEnabled &&
          collateralAssetToClose &&
          Number(bundleRedeemAmountTokens) > 0
        ) {
          const redeemFullSupply =
            collateralAssetToClose.userSupplyBalanceTokens.isEqualTo(bundleRedeemAmountTokens);
          const redeemUnderlyingMantissa = BigInt(
            convertTokensToMantissa({
              value: new BigNumber(bundleRedeemAmountTokens),
              token: collateralAssetToClose.vToken.underlyingToken,
            }).toFixed(),
          );
          return executeRouterClosePositionQueue({
            repayAmountMantissa: BigInt(amountMantissa.toFixed()),
            repaySource,
            repayVToken: asset.vToken,
            redeemUnderlyingAmountMantissa: redeemUnderlyingMantissa,
            redeemFullSupply,
            redeemVToken: collateralAssetToClose.vToken,
            redeemUnwrapNative: true,
            slippagePercentage: userSlippageTolerancePercentage,
            repayFullLoan,
          });
        }

        let paymentSource: RouterRepaySource = { kind: 'native-tao' };
        if (isUsingBittensorNativeAlphaSource) {
          if (!selectedBittensorNativeAlphaSource) {
            throw new Error('Missing selected native alpha source');
          }
          paymentSource = {
            kind: 'native-alpha',
            source: selectedBittensorNativeAlphaSource,
          };
        }
        return executeRouterRepayQueue({
          amountMantissa: BigInt(amountMantissa.toFixed()),
          paymentSource,
          slippagePercentage: userSlippageTolerancePercentage,
          vToken: asset.vToken,
          repayFullLoan,
        });
      }

      // Native Bittensor sources can only be repaid through the position
      // router; the plain repay path below would send a native-denominated
      // amount to the vToken. Native TAO with wrapping enabled is deliberately
      // exempt — that combination is served by the legacy NativeTokenGateway
      // wrap+repay path (onRepay with wrap: true). Unreachable while the
      // positionRouter flag covers every chain that produces native sources —
      // guards against flag drift.
      if (
        isUsingBittensorNativeAlphaSource ||
        (isUsingBittensorNativeTaoSource && !isWrappingNativeToken)
      ) {
        throw new VError({ type: 'unexpected', code: 'somethingWentWrong' });
      }

      // Handle repay flow
      if (!isUsingSwap) {
        return onRepay({
          amountMantissa,
          repayFullLoan,
          wrap: isWrappingNativeToken,
          vToken: asset.vToken,
          poolName: pool.name,
          poolComptrollerContractAddress: pool.comptrollerAddress,
        });
      }

      // Throw an error if we're meant to execute a swapQuote but no swapQuote was
      // passed through props. This should never happen since the form is
      // disabled while swapQuote infos are being fetched, but we add this logic
      // as a safeguard
      if (!swapQuote) {
        throw new VError({ type: 'unexpected', code: 'somethingWentWrong' });
      }

      // Handle swapQuote and repay flow
      return onSwapAndRepay({
        repayFullLoan,
        swapQuote,
        poolName: pool.name,
        poolComptrollerContractAddress: pool.comptrollerAddress,
        vToken: asset.vToken,
        isSwappingNative: !!formValues.fromToken.isNative,
      });
    },
    [
      isUsingSwap,
      onRepay,
      isWrappingNativeToken,
      onSwapAndRepay,
      asset.vToken,
      pool.name,
      pool.comptrollerAddress,
      formValues.fromToken,
      useRouterFlow,
      isUsingBittensorNativeAlphaSource,
      isUsingBittensorNativeTaoSource,
      selectedBittensorNativeAlphaSource,
      executeRouterRepayQueue,
      userSlippageTolerancePercentage,
      canBundleClosePosition,
      bundleRedeemEnabled,
      bundleRedeemAmountTokens,
      collateralAssetToClose,
      executeRouterClosePositionQueue,
    ],
  );

  const debouncedFormAmountTokens = useDebounceValue(formValues.amountTokens);
  const fromTokenAmountTokens = new BigNumber(debouncedFormAmountTokens || 0);

  const fixedRepayBorrowBalanceTokens = formValues.fixedRepayPercentage
    ? new BigNumber(
        calculatePercentageOfUserBorrowBalance({
          userBorrowBalanceTokens: asset.userBorrowBalanceTokens,
          token: asset.vToken.underlyingToken,
          percentage:
            formValues.fixedRepayPercentage === 100
              ? // Buff amount if we are repaying a full loan to account for accrued interests while
                // the transaction is being mined
                formValues.fixedRepayPercentage + FULL_REPAYMENT_BUFFER_PERCENTAGE
              : formValues.fixedRepayPercentage,
        }),
      )
    : undefined;

  const sharedSwapQuoteProps = {
    fromToken: formValues.fromToken,
    toToken: asset.vToken.underlyingToken,
    toTokenAmountTokens: fixedRepayBorrowBalanceTokens || new BigNumber(0),
    recipientAddress: swapRouterContractAddress || NULL_ADDRESS,
    slippagePercentage: userSlippageTolerancePercentage,
  };

  const {
    data: swapQuoteData,
    error: swapQuoteError,
    isLoading: swapQuoteLoading,
  } = useGetSwapQuote(
    formValues.fixedRepayPercentage && fixedRepayBorrowBalanceTokens
      ? {
          ...sharedSwapQuoteProps,
          minToTokenAmountTokens: fixedRepayBorrowBalanceTokens,
          direction: 'approximate-out',
        }
      : {
          ...sharedSwapQuoteProps,
          fromTokenAmountTokens,
          direction: 'exact-in',
        },
    {
      enabled:
        isUsingSwap &&
        !!swapRouterContractAddress &&
        (formValues.fixedRepayPercentage
          ? !!fixedRepayBorrowBalanceTokens?.isGreaterThan(0)
          : fromTokenAmountTokens.isGreaterThan(0)),
    },
  );

  const swapQuote = swapQuoteData?.swapQuote;

  const approval: Approval | undefined = spenderAddress
    ? {
        type: 'token',
        token: formValues.fromToken,
        spenderAddress,
      }
    : undefined;

  let fromTokenUserWalletBalanceTokens: BigNumber | undefined = asset.userWalletBalanceTokens;

  const fromTokenUserWalletBalanceMantissa = tokenBalances.find((item) =>
    areTokensEqual(item.token, formValues.fromToken),
  );

  fromTokenUserWalletBalanceTokens =
    fromTokenUserWalletBalanceMantissa &&
    convertMantissaToTokens({
      value: fromTokenUserWalletBalanceMantissa.balanceMantissa,
      token: fromTokenUserWalletBalanceMantissa.token,
    });

  let repayToTokenAmountTokens = new BigNumber(debouncedFormAmountTokens || 0);

  if (isUsingSwap && formValues.fixedRepayPercentage && fixedRepayBorrowBalanceTokens) {
    repayToTokenAmountTokens = fixedRepayBorrowBalanceTokens;
  } else if (isUsingSwap) {
    repayToTokenAmountTokens = swapQuote ? getSwapToTokenAmount(swapQuote) : new BigNumber(0);
  } else if (
    (isUsingBittensorNativeAlphaSource || isUsingBittensorNativeTaoSource) &&
    nativeBorrowExchangeRate !== undefined &&
    nativeBorrowDecimals !== undefined &&
    nativeBorrowExchangeRate > 0n
  ) {
    // Balance simulation expects underlying (wrapper) units, but the native
    // input is native-denominated. Invert the debt conversion:
    // wrapperTokens = nativeTokens * 10^nativeDecimals / exchangeRate.
    // Capped at the wrapper debt: the ROUND_CEIL applied to the native-side
    // debt can otherwise overshoot it by conversion dust, which would trip
    // the HIGHER_THAN_REPAY_BALANCE validation on a legitimate 100% repay
    // (the router refunds any on-chain over-payment).
    repayToTokenAmountTokens = BigNumber.min(
      repayToTokenAmountTokens
        .times(new BigNumber(10).pow(nativeBorrowDecimals))
        .dividedBy(nativeBorrowExchangeRate.toString())
        .dp(asset.vToken.underlyingToken.decimals, BigNumber.ROUND_DOWN),
      asset.userBorrowBalanceTokens,
    );
  }

  const isRepayingFullLoan = formValues.fixedRepayPercentage === 100;

  const balanceMutations: AssetBalanceMutation[] = [
    {
      type: 'asset',
      vTokenAddress: asset.vToken.address,
      action: 'repay',
      amountTokens:
        isUsingSwap && isRepayingFullLoan
          ? asset.userBorrowBalanceTokens
          : repayToTokenAmountTokens,
    },
  ];

  const { data: getSimulatedPoolData } = useSimulateBalanceMutations({
    pool,
    balanceMutations,
  });
  const simulatedPool = getSimulatedPoolData?.pool;

  // Native router full repays buffer the spend on-chain
  // (FULL_REPAYMENT_BUFFER_PERCENTAGE, plus RAO flooring for native TAO), so a
  // wallet holding exactly the un-buffered debt clears the plain
  // amount-vs-balance check yet reverts on the router spend. Gate validation
  // against the router's true requirement (shared helper mirrors the MAX button
  // guard and useRouterRepayQueue); undefined for every other path = no
  // behavior change.
  const fullRepayWalletBalanceRequirementTokens =
    formValues.fixedRepayPercentage === 100 &&
    (isUsingBittensorNativeAlphaSource || isUsingBittensorNativeTaoSource)
      ? getFullRepayWalletRequirementTokens({
          nativeDebtTokens: fromTokenUserBorrowBalanceTokens,
          nativeToken: formValues.fromToken,
          appliesRaoFloor: isUsingBittensorNativeTaoSource,
        })
      : undefined;

  const { handleSubmit, isFormValid, formError } = useForm({
    asset,
    pool,
    balanceMutations,
    toVToken: asset.vToken,
    fromTokenUserWalletBalanceTokens,
    fromTokenUserBorrowBalanceTokens,
    fullRepayWalletBalanceRequirementTokens,
    fromTokenWalletSpendingLimitTokens,
    isUsingSwap,
    swapQuote,
    swapQuoteErrorCode: swapQuoteError?.code,
    onSubmitSuccess,
    onSubmit,
    formValues,
    setFormValues,
    simulatedPool,
    isFromTokenApproved,
  });

  const readableFromTokenUserWalletBalanceTokens = formatTokensToReadableValue({
    value: fromTokenUserWalletBalanceTokens,
    token: formValues.fromToken,
  });

  let limitTokens = BigNumber.min(
    fromTokenUserBorrowBalanceTokens,
    fromTokenUserWalletBalanceTokens || 0,
  );

  // If using swap, set input amount to wallet balance
  if (isUsingSwap) {
    limitTokens = new BigNumber(fromTokenUserWalletBalanceTokens || 0);
  }

  // If user has set a spending limit, consider it
  if (fromTokenWalletSpendingLimitTokens?.isGreaterThan(0)) {
    limitTokens = BigNumber.min(limitTokens || 0, fromTokenWalletSpendingLimitTokens);
  }

  const captureAmountSetAnalyticEvent = ({
    amountTokens,
    maxSelected,
    selectedPercentage,
  }: {
    amountTokens: BigNumber | string;
    maxSelected: boolean;
    selectedPercentage?: number;
  }) => {
    if (Number(amountTokens.toString()) > 0) {
      captureAnalyticEvent(
        'repay_amount_set',
        {
          poolName: pool.name,
          assetSymbol: asset.vToken.underlyingToken.symbol,
          usdAmount: calculateAmountDollars({
            amountTokens,
            tokenPriceCents: asset.tokenPriceCents,
          }),
          maxSelected,
          selectedPercentage,
        },
        {
          debounced: true,
        },
      );
    }
  };

  const handleRightMaxButtonClick = () => {
    const updatedValues: Partial<FormValues> = {
      fixedRepayPercentage: undefined,
    };

    // Native router full repays are buffered on-chain
    // (FULL_REPAYMENT_BUFFER_PERCENTAGE, plus RAO flooring for native TAO), so
    // the wallet must cover the router's true requirement or the transaction
    // fails late with an exact balance. Shared helper mirrors the validation
    // gate and useRouterRepayQueue.
    const isUsingNativeSource =
      isUsingBittensorNativeAlphaSource || isUsingBittensorNativeTaoSource;
    const fullRepayRequirementTokens = isUsingNativeSource
      ? getFullRepayWalletRequirementTokens({
          nativeDebtTokens: fromTokenUserBorrowBalanceTokens,
          nativeToken: formValues.fromToken,
          appliesRaoFloor: isUsingBittensorNativeTaoSource,
        })
      : fromTokenUserBorrowBalanceTokens;
    const canRepayFullLoan =
      // If user is using swap, we don't know if they can repay the full loan after conversion
      !isUsingSwap &&
      limitTokens.isGreaterThan(0) &&
      (isUsingNativeSource
        ? new BigNumber(fromTokenUserWalletBalanceTokens || 0).isGreaterThanOrEqualTo(
            fullRepayRequirementTokens,
          )
        : limitTokens.isGreaterThanOrEqualTo(fullRepayRequirementTokens));

    if (canRepayFullLoan) {
      // If user can repay full loan, set fixed repay percentage to 100%
      updatedValues.fixedRepayPercentage = 100;
    } else {
      updatedValues.amountTokens = limitTokens.toFixed();
    }

    captureAmountSetAnalyticEvent({
      amountTokens:
        canRepayFullLoan && !isUsingSwap
          ? calculatePercentageOfUserBorrowBalance({
              userBorrowBalanceTokens: fromTokenUserBorrowBalanceTokens,
              token: formValues.fromToken,
              percentage: 100,
            })
          : limitTokens,
      maxSelected: true,
    });

    // Update form values with new values
    setFormValues((currentFormValues) => ({
      ...currentFormValues,
      ...updatedValues,
    }));
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-4">
        {isIntegratedSwapFeatureEnabled || canWrapNativeToken || tokenBalances.length > 1 ? (
          <SelectTokenTextField
            data-testid={TEST_IDS.selectTokenTextField}
            selectedToken={formValues.fromToken}
            value={formValues.amountTokens}
            hasError={
              !isSubmitting &&
              !!formError &&
              formError.code !== 'REQUIRES_RISK_ACKNOWLEDGEMENT' &&
              formError.code !== 'REQUIRES_SWAP_PRICE_IMPACT_ACKNOWLEDGEMENT' &&
              formError.code !== 'MISSING_DATA' &&
              formError.code !== 'EMPTY_TOKEN_AMOUNT' &&
              formError.code !== 'HIGHER_THAN_REPAY_BALANCE'
            }
            disabled={!isUserConnected || isSubmitting}
            onChange={(amountTokens) => {
              captureAmountSetAnalyticEvent({
                amountTokens,
                maxSelected: false,
              });

              setFormValues((currentFormValues) => ({
                ...currentFormValues,
                amountTokens,
                // Reset selected fixed percentage
                fixedRepayPercentage: undefined,
              }));
            }}
            onChangeSelectedToken={(fromToken) =>
              setFormValues((currentFormValues) => ({
                ...currentFormValues,
                amountTokens: initialFormValues.amountTokens,
                fromToken,
              }))
            }
            rightMaxButton={{
              label: t('operationForm.rightMaxButtonLabel'),
              onClick: handleRightMaxButtonClick,
            }}
            tokenBalances={tokenBalances}
            description={
              !isSubmitting && !!formError?.message ? (
                <p className="text-red">{formError.message}</p>
              ) : undefined
            }
          />
        ) : (
          <TokenTextField
            name="amountTokens"
            token={asset.vToken.underlyingToken}
            value={formValues.amountTokens}
            onChange={(amountTokens) => {
              captureAmountSetAnalyticEvent({
                amountTokens,
                maxSelected: false,
              });

              return setFormValues((currentFormValues) => ({
                ...currentFormValues,
                amountTokens,
                // Reset selected fixed percentage
                fixedRepayPercentage: undefined,
              }));
            }}
            disabled={!isUserConnected || isSubmitting}
            rightMaxButton={{
              label: t('operationForm.rightMaxButtonLabel'),
              onClick: handleRightMaxButtonClick,
            }}
            data-testid={TEST_IDS.tokenTextField}
            hasError={
              isUserConnected && !isSubmitting && !!formError && Number(formValues.amountTokens) > 0
            }
            description={
              isUserConnected && !isSubmitting && !!formError?.message ? (
                <p className="text-red">{formError.message}</p>
              ) : undefined
            }
          />
        )}

        <div className="flex gap-x-2">
          {PRESET_PERCENTAGES.map((percentage) => (
            <QuaternaryButton
              key={`select-button-${percentage}`}
              className="flex-1"
              size="xs"
              rounded
              active={percentage === formValues.fixedRepayPercentage}
              disabled={!isUserConnected || asset.userBorrowBalanceTokens.isEqualTo(0)}
              onClick={() => {
                if (!isUsingSwap) {
                  const amountTokens = calculatePercentageOfUserBorrowBalance({
                    userBorrowBalanceTokens: fromTokenUserBorrowBalanceTokens,
                    token: formValues.fromToken,
                    percentage,
                  });

                  captureAmountSetAnalyticEvent({
                    amountTokens,
                    maxSelected: false,
                    selectedPercentage: percentage,
                  });
                }

                setFormValues((currentFormValues) => ({
                  ...currentFormValues,
                  fixedRepayPercentage: percentage,
                }));
              }}
            >
              {formatPercentageToReadableValue(percentage)}
            </QuaternaryButton>
          ))}
        </div>

        {isUserConnected ? (
          <>
            <Notice
              isFormValid={isFormValid}
              isSubmitting={isSubmitting}
              isRepayingFullLoan={isRepayingFullLoan}
              priceImpactPercentage={swapQuote?.priceImpactPercentage}
            />

            <div className="space-y-2">
              <LabeledInlineContent label={t('operationForm.walletBalance')}>
                {readableFromTokenUserWalletBalanceTokens}
              </LabeledInlineContent>

              <SpendingLimit
                token={formValues.fromToken}
                walletBalanceTokens={fromTokenUserWalletBalanceTokens}
                walletSpendingLimitTokens={fromTokenWalletSpendingLimitTokens}
                onRevoke={revokeFromTokenWalletSpendingLimit}
                isRevokeLoading={isRevokeFromTokenWalletSpendingLimitLoading}
                data-testid={TEST_IDS.spendingLimit}
              />
            </div>
          </>
        ) : (
          <ApyBreakdown pool={pool} balanceMutations={balanceMutations} />
        )}

        {canBundleClosePosition && collateralAssetToClose && (
          <>
            <LabeledInlineContent
              label={t('operationForm.closePositionToggle.label', {
                context: collateralWrapperKind,
              })}
              tooltip={t('operationForm.closePositionToggle.tooltip', {
                context: collateralWrapperKind,
                tokenSymbol: collateralAssetToClose.vToken.underlyingToken.symbol,
              })}
            >
              <Toggle
                onChange={() => setBundleRedeemEnabled((current) => !current)}
                value={bundleRedeemEnabled}
                disabled={isSubmitting}
              />
            </LabeledInlineContent>

            {bundleRedeemEnabled && (
              <>
                <TokenTextField
                  name="bundleRedeemAmountTokens"
                  token={collateralAssetToClose.vToken.underlyingToken}
                  value={bundleRedeemAmountTokens}
                  onChange={setBundleRedeemAmountTokens}
                  disabled={!isUserConnected || isSubmitting}
                />

                {bundleRedeemExceedsBalance && (
                  <NoticeWarning
                    description={t('operationForm.error.exceedsCollateralBalance', {
                      max: formatTokensToReadableValue({
                        value: collateralAssetToClose.userSupplyBalanceTokens,
                        token: collateralAssetToClose.vToken.underlyingToken,
                      }),
                    })}
                  />
                )}
              </>
            )}
          </>
        )}

        <TransactionQueue
          items={bundleRedeemEnabled ? routerClosePositionQueueItems : routerRepayQueueItems}
        />

        <Footer
          analyticVariant="repay_with_wallet_form"
          balanceMutations={balanceMutations}
          pool={pool}
          submitButtonLabel={t('operationForm.submitButtonLabel.repay')}
          isFormValid={isFormValid && !bundleRedeemAmountMissing && !bundleRedeemExceedsBalance}
          simulatedPool={simulatedPool}
          approval={approval}
          swapFromToken={isUsingSwap ? formValues.fromToken : undefined}
          swapToToken={isUsingSwap ? asset.vToken.underlyingToken : undefined}
          swapQuote={swapQuote}
          isUserAcknowledgingHighPriceImpact={formValues.acknowledgeHighPriceImpact}
          setAcknowledgeHighPriceImpact={(acknowledgeHighPriceImpact) =>
            setFormValues((currentFormValues) => ({
              ...currentFormValues,
              acknowledgeHighPriceImpact,
            }))
          }
          isLoading={isSubmitting || swapQuoteLoading}
        />
      </div>
    </form>
  );
};

export default RepayWithWalletBalanceForm;
